/**
 * The desktop app, when the dashboard is running inside it (DESKTOP §3, §7).
 *
 * `qaDesktop()` answers `undefined` in a browser and the bridge in the app's
 * window, which is the whole of the feature detection: the Agent APK page
 * shows its second button only when it is defined, so a customer on the web
 * never sees an option their browser cannot honour (D5). The bridge is
 * injected by the app's preload and only on the configured server's origin
 * (D7).
 */

/** What the customer sees under the form while a local build runs. `"key"` is
 *  the first build's several seconds of RSA-4096 generation, which every later
 *  build skips. */
export type BuildStage = "key" | "download" | "stamp" | "sign" | "save";

/** Exactly what `POST /api/v1/agent-builds/local` hands back under `bundle`;
 *  the dashboard passes it through untouched and never reads `config`, which
 *  carries the enrollment key in the clear. */
export interface LocalBuildBundle {
  v: number;
  template: { url: string; sha256: string; size: number; agent_version: string };
  config: unknown;
  modules: unknown;
  design: { document: unknown; files: Array<{ name: string; url: string }> } | null;
}

export interface SigningKeyIdentity {
  /** SHA-256 of the certificate, colon-separated uppercase hex — the same
   *  digest `apksigner verify --print-certs` prints without the colons. */
  fingerprint: string;
  subject: string;
  createdAt: string;
}

export interface QaDesktop {
  version: string;
  platform: "darwin" | "win32" | "linux";
  /** The server this window is bound to. */
  serverUrl: string;
  /** Download, stamp, sign, ask where to save. Resolves once the file is
   *  written, or with `{ cancelled: true }` when the dialog was dismissed.
   *
   *  `report` hands the app the build's id and this session's token so that a
   *  quit during the save dialog — the one ending the page cannot see — still
   *  reaches `/local-result` and no row is left without an outcome (§4.3). */
  buildApk(
    bundle: LocalBuildBundle,
    suggestedFileName: string,
    report: { buildId: string; token: string },
  ): Promise<{ sha256: string; size: number; path: string } | { cancelled: true }>;
  /** Returns its own unsubscribe function, for a React effect to call. */
  onBuildProgress(listener: (stage: BuildStage) => void): () => void;
  signingKey(): Promise<SigningKeyIdentity | null>;
  exportSigningKey(password: string): Promise<{ path: string } | { cancelled: true }>;
  importSigningKey(password: string): Promise<{ fingerprint: string } | { cancelled: true }>;
  changeServer(): Promise<void>;
}

/** Read at render time rather than at import time, so a test can install a
 *  fake `window.qaDesktop` after this module is loaded. */
export function qaDesktop(): QaDesktop | undefined {
  return (globalThis as unknown as { qaDesktop?: QaDesktop }).qaDesktop;
}
